import {useSelector,useDispatch} from 'react-redux'
import {selectAllPayments,getPayments} from './Payments/paymentsSlice'
import useApiStatusDispatch from '../../hooks/useApiStatusDispatch'

import {
	selectAllSessions,
	getSessions,
	selectAllClientSessions
} from './Sessions/sessionsSlice'

import {selectAllActiveSubscriptions,getActiveSubscriptions} from './Subscriptions/activeSubscriptionsSlice'

import {selectAllAvailableSubscriptions,getAvailableSubscriptions} from './Subscriptions/availableSubscriptionsSlice'

import {
	selectAllPackages, 
	fetchPackages, 
	updateCartItem,
	setCartShow as setPackageCartShow,
	selectPackageById,
	selectPackagesByIds,
	clearCart
} from './Packages/packagesSlice'

import {
	setActiveTab as setActiveTabAction,
	getPayoutSummary,
	getPurchaseAvailabilites
} from './financialsDashboardSlice'

import {
	fetchMerchandiseOrders,
	fetchMerchandiseItems,
	selectMerchandiseById,
	selectAllMerchandise,
	selectMerchandiseIds,
	selectItemsByIds,
	selectOrderById,
	selectAllOrders,
	selectOrderIds,
	selectEditor,
	selectCart
} from './Products/merchandiseSlice'

import {
	refreshPurchaseFormTree,
	removeFormPurchaseItem,
	createFormPurchaseItem,
	getPurchaseFormTree
} from './PurchaseForms/slice'

import {
	useCustomForms
} from '../customForms/hooks'

export const useMerchandiseItems = ()=>{
	const dispatch = useDispatch()
	const status = useSelector(state=>state.merchandise.items.status)
	const merchandise = useSelector(selectAllMerchandise)
	const ids = useSelector(selectMerchandiseIds)
	if(status === 'init'){
		dispatch(fetchMerchandiseItems())
	}

	return {status,merchandise,ids}
}

export const useMerchandiseItem = id => {

	const dispatch = useDispatch()
	const status = useSelector(state=>state.merchandise.items.status)
	const item = useSelector(state=>selectMerchandiseById(state,id))

	if(status === 'init') dispatch(fetchMerchandiseItems())

	return {status,item}
}

export const useMerchandiseOrders = ()=>{
	const dispatch = useDispatch()
	const status = useSelector(state=>state.merchandise.orders.status)
	const orders = useSelector(selectAllOrders)
	const ids = useSelector(selectOrderIds)
	if(status === 'init'){
		dispatch(fetchMerchandiseOrders())
	}

	return {status,orders,ids}
}

export const useMerchandiseOrder = id => {

	const dispatch = useDispatch()
	const status = useSelector(state=>state.merchandise.orders.status)
	const order = useSelector(state=>selectOrderById(state,id))

	if(status === 'init') dispatch(fetchMerchandiseOrders())

	return {status,order}
}

export const useMerchandiseEditor = ()=>{
	return useSelector(selectEditor)
}

export const useCart = ()=>{
	const {itemsQtyMap,forms} = useSelector(selectCart)
	const taxPercent = useSelector(
		state => state.settings.app.tax_percent
	)
	const ids = Object.keys(itemsQtyMap)
	const hasItems = ids.length > 0
	const items = useSelector(state=>selectItemsByIds(state,ids))
	const total = (items.reduce((carry,item)=>(
		carry + item.unit_amount*itemsQtyMap[item.id]
	),0)/100)
	const taxAmount = total*taxPercent/100
	const grandTotal = taxAmount + total
	return {itemsQtyMap,hasItems,total,forms,taxAmount,grandTotal}
}

export const usePayments = (opts)=>{
	const dispatch = useDispatch()
	const status = useSelector(state=>state.payments.status)
	const payments = useSelector(selectAllPayments)
	//if payments are still in initial state then trigger a fetch
	if(status==='init') dispatch(getPayments())

	return {status,payments}
}

export const useRefreshPayments = ()=>{
	const dispatch = useDispatch()
	const status = useSelector(state=>state.payments.status)
	const payments = useSelector(selectAllPayments)
	const refresh =  ()=>{
		dispatch(getPayments())
	}
	return refresh
}

export const useSessions = (opts)=>{
	const dispatch = useDispatch()
	const status = useSelector(state=>state.sessions.status)
	const sessions = useSelector(selectAllSessions)
	//will be null for clients but should never be used for clients
	const dateAggregates = useSelector(state=>state.sessions.dateAggregates)
	const clientAggregates = useSelector(selectAllClientSessions)
	//if sessions are still in initial state then trigger a fetch
	if(status==='init') dispatch(getSessions(opts?.payload))

	const totalAvailableCredits = sessions.reduce(
		(t,s)=>t+Number(!s.is_completed),
		0
	)

	return {status,sessions,dateAggregates,clientAggregates,totalAvailableCredits}
}

export const useClientSessionsModal = ()=>{
	const show = useSelector( 
		state => state.sessions.clientSessionsModal.show 
	)
	const aggregates = useSelector(
		state => (
			state.sessions.clientSessionsModal.client_id &&
			state.sessions.clientAggregates.entities[state.sessions.clientSessionsModal.client_id]
		)
	)
	return {show,aggregates}
}

export const useActiveSubscriptions = (opts)=>{
	const dispatch = useDispatch()
	const status = useSelector(state=>state.activeSubscriptions.status)
	const monthlyEstimate = useSelector(state=>state.activeSubscriptions.monthlyEstimate)
	const subscriptions = useSelector(selectAllActiveSubscriptions)
	//if subscriptions are still in initial state then trigger a fetch
	if(status==='init') dispatch(getActiveSubscriptions())

	return {status,subscriptions,monthlyEstimate}
}

export const useAvailableSubscriptions = (opts)=>{
	const dispatch = useDispatch()
	const status = useSelector(state=>state.availableSubscriptions.status)
	const monthlyEstimate = useSelector(state=>state.availableSubscriptions.monthlyEstimate)
	const subscriptions = useSelector(selectAllAvailableSubscriptions)
		.sort(
			(a,b)=>a.sort_order-b.sort_order
		)
	//if subscriptions are still in initial state then trigger a fetch
	if(status==='init') dispatch(getAvailableSubscriptions())

	return {status,subscriptions,monthlyEstimate}
}

export const useFinancialsDashboardTabs = (opts)=>{
	const dispatch = useDispatch()
	const activeTab = useSelector(state=>state.financialsDashboard.activeTab)
	const setActiveTab = tab => { dispatch(setActiveTabAction(tab)) }

	return {activeTab,setActiveTab}
}

export const usePayoutSummary = ()=>{
	const dispatch = useDispatch()
	const {
		status,
		availableBalance,
		pendingBalance,
		past90Total
	} = useSelector(state=>state.financialsDashboard.payouts)
	if(status==='init') dispatch(getPayoutSummary())

	return {
		status,
		availableBalance,
		pendingBalance,
		past90Total
	}
}

export const usePackages = ()=>{
	const dispatch = useDispatch()
	const status = useSelector(state=>state.packages.status)
	const packages = useSelector(selectAllPackages).sort((a,b)=>{
		return a.sort_order - b.sort_order
	})
	if(status==='init') dispatch(fetchPackages())
	return {status,packages}
}

export const usePackage = (id)=>{
	return useSelector(state=>selectPackageById(state,id))
}

export const usePackageCart = ()=>{
	const dispatch = useDispatch()
	const {currencyId,idQtyMap,show} = useSelector(state=>state.packages.cart)
	const ids = Object.keys(idQtyMap)
	const packages = useSelector(state=>selectPackagesByIds(state,ids))
	const mixedCurrencySafeUpdateCartItem = (pkgMeta)=>{
		if(Boolean(currencyId)&&pkgMeta.currencyId!==currencyId){
			window.alert('Cannot add items of mixed currency to cart.')
		}else{
			dispatch(updateCartItem(pkgMeta))
		}
	}
	const totalItems = Object.values(idQtyMap).reduce((carry,item)=>carry+parseInt(item),0)
	const priceTotal = packages.reduce(
		(carry,pkg)=>carry+parseFloat(pkg.package_price)*idQtyMap[pkg.package_id],
		0
	)
	const sessionsTotal = packages.reduce(
		(carry,pkg)=>carry+parseInt(pkg.session_credits),
		0
	)
	const setShow = show => {
		dispatch(setPackageCartShow(show))
	}
	const dispatchClearCart = ()=>{ dispatch(clearCart()) }
	const taxPercent = useSelector(state=>state.settings.app.tax_percent)
	const taxAmount = priceTotal*taxPercent/100
	const grandTotal = taxAmount + priceTotal
	return {
		updateCartItem:mixedCurrencySafeUpdateCartItem,
		clearCart:dispatchClearCart,
		setShow,
		sessionsTotal,
		priceTotal,
		idQtyMap,
		show,
		totalItems,
		currencyId,
		taxAmount,
		grandTotal
	}
}

export const usePurchaseFormsTree = ()=>{
	const dispatch = useDispatch()
	const {status,tree}  = useSelector(state=>state.purchaseForms)
	if ( status === 'init' ) dispatch(getPurchaseFormTree())
	return {status,tree}
}

export const useRefreshPurchaseFormsTree = ()=>{
	const dispatch = useDispatch()
	const refresh =  ()=>{
		dispatch(refreshPurchaseFormTree())
	}
	return refresh
}

export const usePurchaseItemForms = (type,id)=>{
	const {status,tree} = usePurchaseFormsTree()
	//use self invoking funciton to set formIds
	const formIds = (()=>{
		const loaded = status === 'idle'
		const treeHasType = Boolean(tree[type])
		//if loading or tree does not have that type return empty array
		if (!loaded || !treeHasType) return []
		//these don't have deeper nesting
		if(['session','custom_payment'].includes(type)) return tree[type]
		//check if there are form ids for this specific item
		const formIds = tree[type][id]
		if(Boolean(formIds)) return formIds
		//default to returning an empty array
		return []

	})()

	const {status:formsStatus,map,forms:allForms} = useCustomForms()

	//combine the status of forms and purchase forms tree
	const combinedStatus = (
		//if both statuses are idle
		status === 'idle' && formsStatus === 'idle'
		//then the combined status is idle
		? 'idle'
		//else the combined status is pending
		: 'pending'
	);

	const forms = (
		combinedStatus === 'idle'
		? formIds.map(id=>map[id])
		: []
	)

	return {status:combinedStatus,forms,allForms}
}

export const usePurchaseItemsForms = (type,ids)=>{
	const {status,tree} = usePurchaseFormsTree()
	//use self invoking funciton to set formIds
	const formIds = ids.reduce((carry,id)=>{
		const loaded = status === 'idle'
		const treeHasType = Boolean(tree[type])
		//if loading or tree does not have that type return empty array
		if (!loaded || !treeHasType) return carry
		//these don't have deeper nesting
		if(['session','custom_payment'].includes(type)){
			return [
				...carry,
				...tree[type]
			]
		}
		//check if there are form ids for this specific item
		const formIds = tree[type][id]
		if(Boolean(formIds)){ 
			return [
				...carry,
				...formIds
			]
		}
		//default to returning an empty array
		return carry

	},[])

	const {status:formsStatus,map,forms:allForms} = useCustomForms()

	//combine the status of forms and purchase forms tree
	const combinedStatus = (
		//if both statuses are idle
		status === 'idle' && formsStatus === 'idle'
		//then the combined status is idle
		? 'idle'
		//else the combined status is pending
		: 'pending'
	);

	const forms = (
		combinedStatus === 'idle'
		? formIds.map(id=>map[id])
		: []
	)

	return {status:combinedStatus,forms,allForms}
}

export const useCreatePurchaseForm = ()=>{
	const statusDispatch = useApiStatusDispatch()
	const create = (opts)=>{
		statusDispatch(
			createFormPurchaseItem({
				item_type:opts.itemType,
				item_id:opts.itemId,
				form_id:opts.formId
			}),
			'form/waiver',
			'link'
		)
	}
	return create
}

export const useRemovePurchaseForm = ()=>{
	const dispatch = useDispatch()
	const remove = (opts)=>{
		dispatch(removeFormPurchaseItem({
			item_type:opts.itemType,
			item_id:opts.itemId,
			form_id:opts.formId
		}))
	}
	return remove
}

export const usePurchaseAvailabilites = ()=>{
	const dispatch = useDispatch()
	const {status,map:availabilities} = useSelector(
		state=>state.financialsDashboard.purchaseAvailabilities
	)

	if (status==='init') dispatch(getPurchaseAvailabilites())

	return {status,availabilities}
}




